<template>
    <div class="ui-page">
        <ui-page-header title="Análise de Ofício" subtitle="Créditos do Edital do art. 52 por caso, com a situação dos documentos e da análise.">
            <v-btn outlined color="primary" class="mr-2" :disabled="!selected.length && !allSelected" :loading="downloading"
                @click="allSelected ? downloadDocuments(null, true) : downloadDocuments(selected)">
                <v-icon left small>mdi-folder-zip-outline</v-icon>
                Baixar documentos{{ allSelected ? ` (todos: ${dataList.count})` : (selected.length ? ` (${selected.length})` : '') }}
            </v-btn>
            <v-btn v-if="$allowed('create')" id="btnNew" color="primary" depressed :to="newAnalysisRoute">
                <v-icon left small>mdi-plus</v-icon>
                Incluir
            </v-btn>
        </ui-page-header>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-autocomplete id="client" v-model="dataList.filter.client_id" :items="dataset.clients" item-value="id" item-text="name"
                    placeholder="Selecione o caso" dense outlined hide-details class="ui-toolbar__search"
                    no-data-text="Nenhum caso encontrado" @change="onClientChange"></v-autocomplete>
                <v-text-field id="name" v-model="dataList.filter.name" placeholder="Buscar pelo nome do credor" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details class="ui-toolbar__search" :disabled="!dataList.filter.client_id"
                    @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-select id="analyzed" v-model="dataList.filter.analyzed" :items="dataset.analyzedOptions" item-value="id" item-text="name"
                    dense outlined hide-details class="ui-toolbar__field" :disabled="!dataList.filter.client_id" @change="applyFilters"></v-select>
                <v-select id="documents" v-model="dataList.filter.documents" :items="dataset.documentsOptions" item-value="id" item-text="name"
                    dense outlined hide-details class="ui-toolbar__field" :disabled="!dataList.filter.client_id" @change="applyFilters"></v-select>
            </template>

            <ui-empty v-if="!dataList.filter.client_id" icon="mdi-file-search-outline"
                text="Selecione um caso para ver os créditos do Edital do art. 52." />

            <!-- O checkbox do cabecalho seleciona todos os creditos do caso (todas as paginas, com os filtros). -->
            <v-alert v-if="dataList.filter.client_id && allSelected" dense text type="info" class="mb-3">
                Todos os {{ dataList.count }} créditos do caso com os filtros atuais estão selecionados para o download.
                <a href="#" class="ml-2" @click.prevent="keepPageOnly">Manter só os desta página</a>
            </v-alert>

            <v-data-table v-if="dataList.filter.client_id" v-model="selected" show-select item-key="user_creditor_id"
                :headers="dataList.headers" :items="dataList.data" :server-items-length="dataList.count"
                :options.sync="dataList.options" :loading="dataList.loading" disable-sort hide-default-footer
                no-data-text="Nenhum crédito encontrado para os filtros" loading-text="Carregando dados..." class="ui-clickable-rows"
                @click:row="openAnalysis" @toggle-select-all="onToggleSelectAll">

                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.name }}</div>
                    <div class="ui-cell-sub">{{ formatRegistration(item.registration) }}</div>
                </template>

                <template v-slot:[`item.type`]="{ item }">
                    {{ getTypeName(item.type) }}
                </template>

                <template v-slot:[`item.documents`]="{ item }">
                    <ui-badge :text="documentsLabel(item.documents.status)" :tone="documentsTone(item.documents.status)" />
                    <div v-if="item.documents.total" class="ui-cell-sub">{{ item.documents.received }} de {{ item.documents.total }} recebido(s)</div>
                </template>

                <template v-slot:[`item.amount`]="{ item }">
                    {{ formatCurrency(item.amount, item.currency) }}
                </template>

                <template v-slot:[`item.analysis_amount`]="{ item }">
                    <template v-if="item.analyzed">
                        <div>{{ formatCurrency(item.analysis.amount, item.currency) }}</div>
                        <ui-badge v-if="item.has_divergence" text="Divergência" tone="info" class="mt-1" />
                    </template>
                    <ui-badge v-else text="Pendente" tone="warning" />
                </template>

                <template v-slot:[`item.analysis_type`]="{ item }">
                    <span v-if="item.analyzed">{{ getTypeName(item.analysis.type) }}</span>
                    <span v-else class="ui-muted">Pendente</span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="rowActions(item)" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination v-if="dataList.filter.client_id" :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.size"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>

        <!-- Documentos solicitados ao credor neste e nos demais casos -->
        <v-dialog v-model="documentsDialog.open" max-width="760" scrollable>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Documentos do credor</h3>
                        <div class="ui-muted">{{ documentsDialog.creditor ? documentsDialog.creditor.name : '' }}</div>
                    </div>
                    <v-btn icon small @click="documentsDialog.open = false"><v-icon>mdi-close</v-icon></v-btn>
                </div>
                <div class="ui-card__body">
                    <div v-if="documentsDialog.loading" class="text-center py-6">
                        <v-progress-circular indeterminate size="24" width="2" color="primary"></v-progress-circular>
                    </div>
                    <v-alert v-else-if="!documentsDialog.requests.length" type="info" text dense class="mb-0">
                        Nenhum documento foi solicitado a este credor.
                    </v-alert>
                    <div v-else>
                        <div v-for="request in documentsDialog.requests" :key="request.id" class="mb-4">
                            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                                <strong>{{ request.client_name || 'Caso' }}</strong>
                                <span class="ui-muted">solicitado em {{ request.created_at | defaultDate('DD/MM/YYYY') }} · prazo {{ request.deadline | defaultDate('DD/MM/YYYY') }}</span>
                                <ui-badge :text="request.pending_count ? (request.is_closed ? 'Não recebidos' : 'Solicitados') : 'Recebidos'"
                                    :tone="request.pending_count ? (request.is_closed ? 'warning' : 'info') : 'success'" />
                            </div>
                            <v-simple-table dense class="mt-2">
                                <tbody>
                                    <tr v-for="doc in request.documents" :key="doc.id">
                                        <td>{{ doc.name }}</td>
                                        <td style="width: 190px">{{ doc.tag }}</td>
                                        <td class="text-right" style="width: 120px">
                                            <a v-if="doc.file_url" :href="doc.file_url" target="_blank" rel="noopener">Abrir</a>
                                            <span v-else class="ui-muted">—</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </div>
                    </div>
                </div>
                <div class="ui-card__footer">
                    <v-btn text @click="documentsDialog.open = false">Fechar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import Vue from "vue";
import VueLodash from 'vue-lodash'
import _ from 'lodash';
import axios from 'axios';
import creditorTypes from "@/common/data/creditor-types.data"
import creditorClassUtil from "@/common/util/creditor-class.util"

Vue.use(VueLodash)

const DOCUMENT_STATUS = {
    received: { label: 'Recebidos', tone: 'success' },
    requested: { label: 'Solicitados', tone: 'info' },
    not_received: { label: 'Não recebidos', tone: 'warning' },
    not_requested: { label: 'Não solicitados', tone: 'neutral' },
};

export default {
    name: "LetterAnalysisList",

    data: () => ({
        dataset: {
            clients: [],
            creditorTypes: creditorTypes,
            analyzedOptions: [
                { id: '', name: 'Análise: todos' },
                { id: '1', name: 'Créditos analisados' },
                { id: '0', name: 'Créditos não analisados' },
            ],
            documentsOptions: [
                { id: '', name: 'Documentos: todos' },
                { id: 'received', name: 'Documentos recebidos' },
                { id: 'not_received', name: 'Documentos não recebidos' },
            ],
        },
        dataList: {
            filter: {
                client_id: null,
                name: "",
                analyzed: "",
                documents: "",
            },
            headers: [
                { text: "Credor", value: "name" },
                { text: "Classe", value: "type" },
                { text: "Documentos", value: "documents", sortable: false },
                { text: "Valor Edital do art. 52", value: "amount", align: "right" },
                { text: "Valor após análise", value: "analysis_amount", align: "right" },
                { text: "Classe após análise", value: "analysis_type" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            count: 0,
            options: {
                page: 1,
                size: 10,
            },
        },
        documentsDialog: { open: false, loading: false, creditor: null, requests: [] },
        selected: [],
        // Todos os creditos do caso (todas as paginas, com os filtros) marcados pelo cabecalho.
        allSelected: false,
        downloading: false,
    }),

    watch: {
        // Desmarcar um credito da pagina sai do modo "todos".
        selected(value) {
            if (this.allSelected && value.length < this.dataList.data.length) this.allSelected = false;
        },
    },

    computed: {
        hasFilters() {
            return Boolean(this.dataList.filter.name) || Boolean(this.dataList.filter.analyzed) || Boolean(this.dataList.filter.documents);
        },

        newAnalysisRoute() {
            return this.dataList.filter.client_id
                ? { path: '/analise-oficio/novo', query: { from_list: '1', client_id: String(this.dataList.filter.client_id) } }
                : '/analise-oficio/novo';
        },
    },

    async created() {
        await this.loadClients();
        // Volta da tela de analise com o caso que estava selecionado.
        const clientId = Number(this.$route.query.client_id);
        if (clientId) {
            this.dataList.filter.client_id = clientId;
            await this.onClientChange();
        }
    },

    methods: {
        async onClientChange() {
            this.dataList.options.page = 1;
            this.selected = [];
            this.allSelected = false;
            this.dataList.data = [];
            this.dataList.count = 0;
            if (!this.dataList.filter.client_id) return;
            await this.loadCreditorClasses();
            await this.load();
        },

        applyFilters() {
            this.dataList.options.page = 1;
            this.selected = [];
            this.allSelected = false;
            this.load();
        },

        buildSearch() {
            const search = {};
            if (this.dataList.filter.name) search.name = this.dataList.filter.name;
            if (this.dataList.filter.analyzed !== '') search.analyzed = this.dataList.filter.analyzed;
            if (this.dataList.filter.documents) search.documents = this.dataList.filter.documents;
            return search;
        },

        onToggleSelectAll({ value }) {
            this.allSelected = !!value && this.dataList.count > this.dataList.data.length;
        },

        keepPageOnly() {
            this.allSelected = false;
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        clearFilters() {
            this.dataList.filter = { ...this.dataList.filter, name: "", analyzed: "", documents: "" };
            this.applyFilters();
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.size = size;
        },

        pageChanged(page) {
            this.dataList.options.page = page;
            this.load();
        },

        async loadCreditorClasses() {
            // Inclui ocultas: creditos ja classificados mantem o nome da classe.
            this.dataset.creditorTypes = await creditorClassUtil.listLabels(this.dataList.filter.client_id);
        },

        async load() {
            if (!this.dataList.filter.client_id) return;
            this.dataList.loading = true;
            try {
                const search = this.buildSearch();

                const result = await axios.get('/api/admin/official-letters/credits', {
                    params: {
                        client_id: this.dataList.filter.client_id,
                        page: this.dataList.options.page,
                        limit: this.dataList.options.size,
                        search: JSON.stringify(search),
                    }
                });
                this.dataList.data = result.data.content.data;
                this.dataList.count = result.data.content.paging.total;
                // No modo "todos", a nova pagina ja aparece marcada.
                if (this.allSelected) this.selected = [...this.selected, ...this.dataList.data];
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        async loadClients() {
            try {
                const result = await axios.get(`/api/clients`, { params: { start: 1, limit: 100000, search: {} } });
                const data = result.data.content.data || [];
                data.sort((a, b) => a.name.localeCompare(b.name));
                this.dataset.clients = data;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
            }
        },

        rowActions(item) {
            const actions = [
                { text: item.letter_id ? 'Valor, classe e análise' : 'Preencher valor, classe e análise', icon: 'mdi-file-edit-outline', disabled: !this.$check('LetterAnalysis', 'PATCH'), handler: () => this.openAnalysis(item) },
                { text: 'Documentos', icon: 'mdi-file-document-multiple-outline', handler: () => this.openDocuments(item) },
                { text: 'Baixar documentos (ZIP)', icon: 'mdi-folder-zip-outline', disabled: item.documents.received === 0, handler: () => this.downloadDocuments([item]) },
            ];
            if (item.has_divergence) {
                actions.push({ text: 'Abrir divergência', icon: 'mdi-sync-alert', handler: () => this.$router.push(`/divergencias/${item.analysis.user_event_id}`) });
            }
            return actions;
        },

        // Abre a analise existente ou a tela de nova analise ja com o caso e o credor.
        openAnalysis(item) {
            if (!this.$check('LetterAnalysis', 'PATCH')) return;
            if (item.letter_id) {
                this.$router.push({ path: `/analise-oficio/${item.letter_id}`, query: { from_list: '1', client_id: String(this.dataList.filter.client_id) } });
                return;
            }
            this.$router.push({
                path: '/analise-oficio/novo',
                query: {
                    from_list: '1',
                    client_id: String(this.dataList.filter.client_id),
                    user_creditor_id: String(item.user_creditor_id),
                    amount: String(item.amount),
                },
            });
        },

        async openDocuments(item) {
            this.documentsDialog = { open: true, loading: true, creditor: item, requests: [] };
            if (!item.user_id) {
                this.documentsDialog.loading = false;
                return;
            }
            try {
                const result = await axios.get(`/api/admin/users/${item.user_id}/document-requests`);
                const requests = Array.isArray(result.data.content) ? result.data.content : [];
                // Deste caso primeiro.
                const clientId = Number(this.dataList.filter.client_id);
                this.documentsDialog.requests = requests.sort((a, b) => (Number(b.client_id) === clientId) - (Number(a.client_id) === clientId));
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
            } finally {
                this.documentsDialog.loading = false;
            }
        },

        // ZIP com os documentos recebidos dos creditos informados (uma pasta por credor).
        // all = true: todos os creditos do caso com os filtros atuais (a API resolve os ids).
        async downloadDocuments(items, all = false) {
            const ids = all ? [] : (items || []).map((item) => item.user_creditor_id).filter(Boolean);
            if (!all && !ids.length) return;
            this.downloading = true;
            try {
                const params = all
                    ? { client_id: this.dataList.filter.client_id, all: 1, search: JSON.stringify(this.buildSearch()) }
                    : { client_id: this.dataList.filter.client_id, ids: ids.join(',') };
                const response = await axios.get('/api/admin/official-letters/credits/documents', { params, responseType: 'blob' });
                const disposition = response.headers['content-disposition'] || '';
                const match = disposition.match(/filename="?([^";]+)"?/i);
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
                const link = document.createElement('a');
                link.href = url;
                link.download = match ? match[1] : 'documentos-analise-oficio.zip';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                const skipped = Number(response.headers['x-skipped-documents'] || 0);
                if (skipped) this.$dialog.notify.warning(`${skipped} documento(s) não estavam disponíveis no storage e ficaram fora do ZIP.`, { position: 'top-right', timeout: 5000 });
            } catch (error) {
                // Resposta de erro vem como blob: le o JSON para mostrar a mensagem da API.
                let message = 'Não foi possível gerar o ZIP.';
                try { const text = await (error.response && error.response.data && error.response.data.text ? error.response.data.text() : Promise.resolve('')); const json = JSON.parse(text); message = json.content.error.message || message; } catch (e) { /* mantem mensagem padrao */ }
                this.$dialog.notify.error(message, { position: 'top-right', timeout: 5000 });
            } finally {
                this.downloading = false;
            }
        },

        documentsLabel(status) {
            return (DOCUMENT_STATUS[status] || DOCUMENT_STATUS.not_requested).label;
        },

        documentsTone(status) {
            return (DOCUMENT_STATUS[status] || DOCUMENT_STATUS.not_requested).tone;
        },

        getTypeName(typeId) {
            const found = (this.dataset.creditorTypes || []).find((item) => Number(item.id) === Number(typeId));
            return found ? found.name : (typeId ? `Classe ${typeId}` : '—');
        },

        formatCurrency(value, currency = 1) {
            const symbol = Number(currency) === 2 ? 'US$' : (Number(currency) === 3 ? '€' : 'R$');
            return `${symbol} ${Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },

        formatRegistration(value) {
            const clean = String(value || '').replace(/\D/g, '');
            if (clean.length === 11) return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
            if (clean.length === 14) return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
            return value || '';
        },
    },
};
</script>
